// src/context/BillingContext.js
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { useAuth } from "./AuthContext";
import useApi from "../utils/api";
import { useGameSpace } from "./GameSpaceContext";

const BillingContext = createContext(null);
export const useBilling = () => useContext(BillingContext);

export const BillingProvider = ({ children }) => {
  const { isAuthenticated, isLoading, currentUserId } = useAuth();
  const { get, post } = useApi();
  const { gameSpaces } = useGameSpace();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);

  // Check if user owns any gamespace
  const ownsGamespace = gameSpaces.some(gs => gs.space_owner_user_id === currentUserId);

  const refresh = useCallback(async () => {
    if (isLoading || !isAuthenticated || !currentUserId) return;
    
    setLoading(true);
    setError(null);

    try {
      // Always get subscription status
      const status = await get(`${process.env.REACT_APP_API_URL}/billing/subscription/status`);
      setSubscription(status?.subscription || null);

      // Only get billing data if user owns gamespaces
      if (ownsGamespace) {
        const usageData = await get(`${process.env.REACT_APP_API_URL}/billing/billing-summary`);
        setUsage(usageData);
      } else {
        setUsage(null);
      }

    } catch (err) {
      setError(err?.message || "Failed to load billing information");
      setSubscription(null);
      setUsage(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, currentUserId, ownsGamespace, get, isLoading]);

  // Initial load when auth is ready
  useEffect(() => {
    if (isAuthenticated && !isLoading && currentUserId) {
      refresh();
    }
  }, [isAuthenticated, currentUserId, refresh]);

  const checkoutCreator = async () => {
    try {
      const { url } = await post(`${process.env.REACT_APP_API_URL}/billing/checkout/creator`);
      if (url) window.location.assign(url);
    } catch (err) {
      setError(err?.message || "Failed to create checkout session");
    }
  };

  const openPortal = async () => {
    
    if (isLoading || !isAuthenticated || !currentUserId) {
      setError("Not authenticated to see portal");

      return;
    }
    
    try {
      const response = await post(`${process.env.REACT_APP_API_URL}/billing/portal`);
      if (response && response.url) {
        window.location.assign(response.url);
      } else {
        setError("Invalid response from billing portal");
      }
    } catch (err) {
      console.error("Portal error:", err);
      setError(err?.message || "Failed to open billing portal. Please check your Stripe configuration.");
    }
  };

  const cancelAtPeriodEnd = useCallback(async () => {
    try {
      await post(`${process.env.REACT_APP_API_URL}/billing/subscription/cancel`);
      await refresh();
    } catch (err) {
      setError(err?.message || "Failed to cancel subscription");
    }
  }, [refresh, post]);

  const reactivate = useCallback(async () => {
    try {
      await post(`${process.env.REACT_APP_API_URL}/billing/subscription/reactivate`);
      await refresh();
    } catch (err) {
      setError(err?.message || "Failed to reactivate subscription");
    }
  }, [refresh, post]);

  // Add this computed value
  const billingSummary = useMemo(() => {

    if (!usage) return null;
    
    return {
      subscription,
      usage,
      billing_items: usage.gamespaces || []
    };
  }, [subscription, usage]);

  const normalized = useMemo(() => {
    if (!subscription && !usage) return null;
    
    
    const usageData = usage || {};
    const subscriptionData = subscription;
    
    // Use freemium_info for accurate free tier data
    const freemiumInfo = usageData.freemium_info || {};
    
    // Determine tier based on subscription status
    let tier = "free";
    let isOverLimit = false;
    let isAtFreeTierLimit = false;
    
    if (subscriptionData && subscriptionData.status) {
      const isActive = subscriptionData.status === "active" || subscriptionData.status === "past_due" || subscriptionData.status === "trialing";
      
      if (isActive) {
        tier = "creator_pro";
        isOverLimit = false;
        isAtFreeTierLimit = false;
      } else {
        // Fall back to freemium_info for inactive subscriptions
        tier = freemiumInfo.tier ?? "free";
        isOverLimit = freemiumInfo.is_over_limit ?? false;
        isAtFreeTierLimit = freemiumInfo.is_over_limit ?? false;
      }
    } else {
      // No subscription - use freemium_info
      tier = freemiumInfo.tier ?? "free";
      isOverLimit = freemiumInfo.is_over_limit ?? false;
      isAtFreeTierLimit = freemiumInfo.is_over_limit ?? false;
    }
    
    return {
      tier,
      freeSeatsIncluded: freemiumInfo.free_seats_used + freemiumInfo.remaining_free_seats || 1, // Calculate from freemium_info
      freeSeatsUsed: freemiumInfo.free_seats_used ?? 0,
      creatorProPlanLimit: usageData.creator_pro_plan_limit ?? 3,
      canAddMoreCreators: freemiumInfo.remaining_free_seats > 0,
      remainingFreeSeats: freemiumInfo.remaining_free_seats ?? 0,
      isOverLimit,
      isAtFreeTierLimit,
      upgradeMessage: tier === "free" ? 
        "Upgrade to Creator Pro Plan for up to 3 creators and premium features" :
        "You're using the Creator Pro Plan with up to 3 creators",
      totalCreatorSeats: usageData.total_creator_seats ?? 0,
      billingItems: usageData.billing_items || [],
      hasActiveSubscription: subscriptionData && (subscriptionData.status === "active" || subscriptionData.status === "past_due" || subscriptionData.status === "trialing"),
      isCanceling: subscriptionData?.cancel_at_period_end === true,
      canReactivate: false,
      canCheckout: true,
      raw: { subscription: subscriptionData, usage: usageData },
    };
  }, [subscription, usage]);


  const value = {
    loading,
    error,
    subscription,
    usage,
    billingSummary,
    billing: normalized,
    refresh,
    checkoutCreator,
    openPortal,
    cancelAtPeriodEnd,
    reactivate
  };

  return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
};